import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Calendar as CalendarIcon,
    ChevronLeft,
    ChevronRight,
    Clock,
    CheckCircle2,
    Target,
    TrendingUp,
    Flame,
    ChevronDown,
    ChevronUp,
    MessageSquare,
    Tag,
} from "lucide-react";
import type { Task, DailyStats } from "@/types";
import {
    formatHoursMinutes,
    getDateKey,
    getElapsedSeconds,
    isSameDay,
} from "@/utils/helpers";
import { TagDisplay } from "@/components/TagInput";
import { TaskComments } from "@/components/TaskComments";

type StatsPeriod = "day" | "week" | "month" | "year";

interface CalendarViewProps {
    tasks: Task[];
    dailyStats: Record<string, DailyStats>;
    onSelectDate: (date: Date) => void;
}

// Sub-component for displaying task in calendar view with expandable details
function CalendarTaskItem({ task }: { task: Task }) {
    const [expanded, setExpanded] = useState(false);
    const hasTags = task.tags && task.tags.length > 0;
    const hasComments = task.comments && task.comments.length > 0;
    const hasDetails = hasTags || hasComments;

    return (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 overflow-hidden">
            <button
                onClick={() => hasDetails && setExpanded(!expanded)}
                className={`w-full flex items-center justify-between p-2 ${
                    hasDetails
                        ? "cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30"
                        : "cursor-default"
                }`}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm truncate">{task.name}</span>
                    {hasTags && (
                        <Tag className="w-3 h-3 text-muted-foreground shrink-0" />
                    )}
                    {hasComments && (
                        <div className="flex items-center gap-0.5 text-muted-foreground shrink-0">
                            <MessageSquare className="w-3 h-3" />
                            <span className="text-xs">
                                {task.comments!.length}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground">
                        {formatHoursMinutes(
                            Math.floor(task.elapsedSeconds / 60)
                        )}
                    </span>
                    {hasDetails &&
                        (expanded ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        ))}
                </div>
            </button>

            {expanded && hasDetails && (
                <div className="px-3 pb-3 pt-1 space-y-2 border-t border-green-100 dark:border-green-800/30">
                    {hasTags && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <Tag className="w-3 h-3 text-muted-foreground" />
                            <TagDisplay tags={task.tags!} size="sm" />
                        </div>
                    )}
                    {hasComments && (
                        <TaskComments
                            comments={task.comments!}
                            maxVisible={3}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export function CalendarView({
    tasks,
    dailyStats,
    onSelectDate,
}: CalendarViewProps) {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>("week");

    const daysInMonth = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startPadding = firstDay.getDay();
        const days: (Date | null)[] = [];

        // Add padding for days before the first of the month
        for (let i = 0; i < startPadding; i++) {
            days.push(null);
        }

        // Add all days of the month
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(year, month, i));
        }

        return days;
    }, [currentMonth]);

    const getStatsForDate = (date: Date) => {
        const dateKey = getDateKey(date);

        // Check saved daily stats first
        if (dailyStats[dateKey]) {
            return dailyStats[dateKey];
        }

        // Otherwise calculate from tasks
        const dayTasks = tasks.filter(
            (t) => !t.isBacklog && isSameDay(new Date(t.createdAt), date)
        );
        const completedTasks = dayTasks.filter((t) => t.status === "completed");
        const totalWorkedSeconds = dayTasks.reduce(
            (sum, t) => sum + getElapsedSeconds(t),
            0
        );

        return {
            date: dateKey,
            totalTasks: dayTasks.length,
            completedTasks: completedTasks.length,
            totalWorkedSeconds,
            totalEstimatedMinutes: dayTasks.reduce(
                (sum, t) => sum + t.estimatedMinutes,
                0
            ),
        };
    };

    const prevMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
        );
    };

    const nextMonth = () => {
        setCurrentMonth(
            new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
        );
    };

    const today = new Date();
    const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];

    // Get selected day stats for detail view
    const [selectedDay, setSelectedDay] = useState<Date | null>(null);
    const selectedStats = selectedDay ? getStatsForDate(selectedDay) : null;
    const selectedDayTasks = selectedDay
        ? tasks.filter(
              (t) =>
                  !t.isBacklog &&
                  isSameDay(new Date(t.createdAt), selectedDay) &&
                  t.status === "completed"
          )
        : [];

    // Calculate aggregated stats based on period
    const aggregatedStats = useMemo(() => {
        const now = new Date();
        let startDate: Date;
        let endDate = now;
        let periodLabel = "";

        switch (statsPeriod) {
            case "day":
                startDate = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate()
                );
                periodLabel = "Today";
                break;
            case "week":
                const dayOfWeek = now.getDay();
                startDate = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate() - dayOfWeek
                );
                periodLabel = "This Week";
                break;
            case "month":
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                periodLabel = monthNames[now.getMonth()];
                break;
            case "year":
                startDate = new Date(now.getFullYear(), 0, 1);
                periodLabel = String(now.getFullYear());
                break;
        }

        let totalTasks = 0;
        let completedTasks = 0;
        let totalWorkedSeconds = 0;
        let daysWithTasks = 0;
        let currentStreak = 0;

        // Iterate through each day in the period
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
            const stats = getStatsForDate(currentDate);
            if (stats.totalTasks > 0) {
                daysWithTasks++;
            }
            totalTasks += stats.totalTasks;
            completedTasks += stats.completedTasks;
            totalWorkedSeconds += stats.totalWorkedSeconds;
            currentDate.setDate(currentDate.getDate() + 1);
        }

        // Calculate current streak (consecutive days with completed tasks)
        const checkDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
        );
        while (true) {
            const stats = getStatsForDate(checkDate);
            if (stats.completedTasks > 0) {
                currentStreak++;
                checkDate.setDate(checkDate.getDate() - 1);
            } else if (isSameDay(checkDate, now)) {
                // If today has no completions yet, check from yesterday
                checkDate.setDate(checkDate.getDate() - 1);
            } else {
                break;
            }
        }

        const completionRate =
            totalTasks > 0
                ? Math.round((completedTasks / totalTasks) * 100)
                : 0;
        const avgDailyTasks =
            daysWithTasks > 0
                ? Math.round((completedTasks / daysWithTasks) * 10) / 10
                : 0;

        return {
            periodLabel,
            totalTasks,
            completedTasks,
            totalWorkedSeconds,
            completionRate,
            avgDailyTasks,
            currentStreak,
        };
    }, [statsPeriod, tasks, dailyStats]);

    return (
        <div className="space-y-6">
            {/* Stats Summary Card */}
            <Card className="border-0 shadow-lg bg-linear-to-br from-blue-50 to-purple-50 dark:from-blue-950/50 dark:to-purple-950/50">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            {aggregatedStats.periodLabel} Stats
                        </CardTitle>
                        <div className="flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
                            {(
                                [
                                    "day",
                                    "week",
                                    "month",
                                    "year",
                                ] as StatsPeriod[]
                            ).map((period) => (
                                <Button
                                    key={period}
                                    variant={
                                        statsPeriod === period
                                            ? "default"
                                            : "ghost"
                                    }
                                    size="sm"
                                    className={`h-7 px-3 text-xs capitalize ${
                                        statsPeriod === period
                                            ? "bg-blue-600 text-white hover:bg-blue-700"
                                            : "hover:bg-gray-100 dark:hover:bg-gray-700"
                                    }`}
                                    onClick={() => setStatsPeriod(period)}
                                >
                                    {period}
                                </Button>
                            ))}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {/* Completed Tasks */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                            <div className="flex items-center gap-2 mb-1">
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Completed
                                </span>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                {aggregatedStats.completedTasks}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                of {aggregatedStats.totalTasks} tasks
                            </div>
                        </div>

                        {/* Time Worked */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                            <div className="flex items-center gap-2 mb-1">
                                <Clock className="w-4 h-4 text-blue-500" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Time
                                </span>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                {formatHoursMinutes(
                                    Math.floor(
                                        aggregatedStats.totalWorkedSeconds / 60
                                    )
                                )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                worked
                            </div>
                        </div>

                        {/* Completion Rate */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                            <div className="flex items-center gap-2 mb-1">
                                <Target className="w-4 h-4 text-purple-500" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Rate
                                </span>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                {aggregatedStats.completionRate}%
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                completion
                            </div>
                        </div>

                        {/* Current Streak */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm">
                            <div className="flex items-center gap-2 mb-1">
                                <Flame className="w-4 h-4 text-orange-500" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Streak
                                </span>
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                {aggregatedStats.currentStreak}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {aggregatedStats.currentStreak === 1
                                    ? "day"
                                    : "days"}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
                <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                            <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <CalendarIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            {monthNames[currentMonth.getMonth()]}{" "}
                            {currentMonth.getFullYear()}
                        </CardTitle>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={prevMonth}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={nextMonth}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Day headers */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                            (day) => (
                                <div
                                    key={day}
                                    className="text-center text-xs font-medium text-muted-foreground py-2"
                                >
                                    {day}
                                </div>
                            )
                        )}
                    </div>

                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-1">
                        {daysInMonth.map((date, index) => {
                            if (!date) {
                                return <div key={`empty-${index}`} />;
                            }

                            const stats = getStatsForDate(date);
                            const isToday = isSameDay(date, today);
                            const isSelected =
                                selectedDay && isSameDay(date, selectedDay);
                            const hasActivity = stats.totalTasks > 0;
                            const completionRate =
                                stats.totalTasks > 0
                                    ? (stats.completedTasks /
                                          stats.totalTasks) *
                                      100
                                    : 0;

                            return (
                                <button
                                    key={date.toISOString()}
                                    onClick={() => setSelectedDay(date)}
                                    className={`
                                        relative aspect-square p-1 rounded-lg text-sm transition-all
                                        hover:bg-slate-100 dark:hover:bg-slate-800
                                        ${
                                            isToday
                                                ? "ring-2 ring-violet-500 ring-offset-2 dark:ring-offset-slate-900"
                                                : ""
                                        }
                                        ${
                                            isSelected
                                                ? "bg-violet-100 dark:bg-violet-900/30"
                                                : ""
                                        }
                                    `}
                                >
                                    <span
                                        className={`
                                        ${
                                            isToday
                                                ? "font-bold text-violet-600 dark:text-violet-400"
                                                : ""
                                        }
                                    `}
                                    >
                                        {date.getDate()}
                                    </span>
                                    {hasActivity && (
                                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                                            <div
                                                className={`w-1.5 h-1.5 rounded-full ${
                                                    completionRate === 100
                                                        ? "bg-green-500"
                                                        : completionRate > 0
                                                        ? "bg-amber-500"
                                                        : "bg-slate-300"
                                                }`}
                                            />
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Selected day details */}
            {selectedDay && selectedStats && (
                <Card className="border-0 shadow-lg">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">
                            {selectedDay.toLocaleDateString("en-US", {
                                weekday: "long",
                                month: "long",
                                day: "numeric",
                            })}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center justify-center gap-1 mb-1">
                                    <Target className="w-4 h-4 text-violet-500" />
                                </div>
                                <div className="text-2xl font-bold">
                                    {selectedStats.completedTasks}/
                                    {selectedStats.totalTasks}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Tasks Done
                                </div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center justify-center gap-1 mb-1">
                                    <Clock className="w-4 h-4 text-blue-500" />
                                </div>
                                <div className="text-2xl font-bold">
                                    {formatHoursMinutes(
                                        Math.floor(
                                            selectedStats.totalWorkedSeconds /
                                                60
                                        )
                                    )}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Time Worked
                                </div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center justify-center gap-1 mb-1">
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                </div>
                                <div className="text-2xl font-bold">
                                    {selectedStats.totalTasks > 0
                                        ? Math.round(
                                              (selectedStats.completedTasks /
                                                  selectedStats.totalTasks) *
                                                  100
                                          )
                                        : 0}
                                    %
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Completion
                                </div>
                            </div>
                        </div>

                        {selectedDayTasks.length > 0 && (
                            <div className="space-y-2">
                                <h4 className="text-sm font-medium text-muted-foreground">
                                    Completed Tasks
                                </h4>
                                {selectedDayTasks.map((task) => (
                                    <CalendarTaskItem
                                        key={task.id}
                                        task={task}
                                    />
                                ))}
                            </div>
                        )}

                        {selectedStats.totalTasks === 0 && (
                            <p className="text-center text-sm text-muted-foreground py-4">
                                No tasks recorded for this day
                            </p>
                        )}

                        {isSameDay(selectedDay, today) && (
                            <Button
                                className="w-full"
                                variant="outline"
                                onClick={() => {
                                    onSelectDate(selectedDay);
                                }}
                            >
                                Go to Today's Tasks
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
